<script lang="ts">
	import type { IconName } from '$lib/components/icons/types';
	import type { PoiFilterId, PoiFilterOptionId } from '../../types';
	import Chip from '$lib/components/atoms/chip/Chip.svelte';
	import MultiIconChip from '$lib/components/atoms/multiIconChip/MultiIconChip.svelte';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';

	export let name: string;
	export let id: PoiFilterId;
	export let options: { id: PoiFilterOptionId; name: string }[];
	export let defaultValue: PoiFilterOptionId[];

	const dispatch = createEventDispatcher<{
		change: { id: PoiFilterId; value: PoiFilterOptionId[] };
	}>();

	const searchParams = getSearchParamsStore(false);

	$: selectedItems = ($searchParams.filters?.[id] || defaultValue) as PoiFilterOptionId[];

	function handleSelectedChange(filterId: PoiFilterOptionId) {
		if (selectedItems.includes(filterId)) {
			dispatch('change', { id, value: selectedItems.filter(items => items !== filterId) });
		} else {
			dispatch('change', { id, value: [...selectedItems, filterId] });
		}
	}

	function getTranslationKey(selectedItems: PoiFilterOptionId[]) {
		if (selectedItems.length === 0) {
			return m.poi_filters__selected_items__none();
		}

		if (selectedItems.length === 1) {
			return m.poi_filters__selected_items__one();
		}

		if (selectedItems.length === options.length) {
			return m.poi_filters__selected_items__all();
		}

		return m.poi_filters__selected_items__many({ count: selectedItems.length });
	}

	const iconNameByPoiFilterOptionId: Partial<Record<PoiFilterOptionId, IconName>> = {
		STANDARD_HOUSEHOLD: 'connector-standard-household',
		MENNEKES: 'connector-mennekes',
		CCS_COMBO: 'connector-ccs2',
		CHADEMO: 'connector-chademo',
		SCAME: 'connector-scame',
		TESLA: 'connector-tesla',
		TYPE_3C: 'connector-type3c',
		BIB_GOURMAND: 'bib-gourmand',
		ONE_STAR: 'michelin-star',
		TWO_STARS: 'michelin-star',
		THREE_STARS: 'michelin-star',
		ONE_KEY: 'michelin-key',
		TWO_KEYS: 'michelin-key',
		THREE_KEYS: 'michelin-key',
		ONE_STAR_TOURISM: 'tourism-star',
		TWO_STARS_TOURISM: 'tourism-star',
		THREE_STARS_TOURISM: 'tourism-star',
		SELECTED_RESTAURANT: '',
	};

	const iconAmountByPoiFilterOptionId: Partial<Record<PoiFilterOptionId, number>> = {
		TWO_STARS: 2,
		THREE_STARS: 3,
		TWO_KEYS: 2,
		THREE_KEYS: 3,
		TWO_STARS_TOURISM: 2,
		THREE_STARS_TOURISM: 3,
	};
</script>

<div>
	<div class="mb-4 flex justify-between items-center color-primary pb-1 text-sm">
		<div>{name}</div>
		<div class="text-bold">
			{getTranslationKey(selectedItems)}
		</div>
	</div>
	<div
		class="flex flex-wrap gap-1"
		role="listbox"
		aria-multiselectable="true"
		aria-orientation="horizontal"
		aria-label={name}
	>
		<!-- eslint-disable-next-line svelte/require-each-key -->
		{#each options as { id, name }}
			{#if iconAmountByPoiFilterOptionId[id]}
				<MultiIconChip
					text={name}
					icon={iconNameByPoiFilterOptionId[id]}
					iconAmount={iconAmountByPoiFilterOptionId[id]}
					onSelectedChange={() => handleSelectedChange(id)}
					selected={selectedItems.includes(id)}
				/>
			{:else}
				<Chip
					scheme="primary"
					text={name}
					icon={iconNameByPoiFilterOptionId[id]}
					onSelectedChange={() => handleSelectedChange(id)}
					selected={selectedItems.includes(id)}
				/>
			{/if}
		{/each}
	</div>
</div>
